import React from "react";
import { useDebounce } from "use-debounce";
import { RnChevronRight } from "@remi/icons/next";
import { isTruthy } from "@remi/ts-utils";

import { useMedia } from "../../hooks";
import type { OptionData } from "../../types/option-data";
import { cn, filterList, flattenOptions, getRaw, getSearchValue, type VerboseOptionData } from "../../utils";
import { RemiButton } from "../RemiButton";
import { RemiCollapsible, RemiCollapsibleContent } from "../RemiCollapsible";
import { RemiPopover, RemiPopoverContent, RemiPopoverTrigger } from "../RemiPopover";
import {
  DropdownCommand,
  DropdownCommandEmpty,
  DropdownCommandGroup,
  DropdownCommandInput,
  DropdownCommandList,
  DropdownCommandSeparator,
} from "./DropdownCommand";
import {
  DropdownModal,
  DropdownModalBody,
  DropdownModalContent,
  DropdownModalFooter,
  DropdownModalHeader,
  DropdownModalTrigger,
} from "./DropdownModal";
import { SelectItem } from "./SelectItem";

interface MultiSelectOptionProps<T extends string> extends React.HTMLAttributes<HTMLDivElement> {
  option: VerboseOptionData<T>;
  setSelectedValues: (value: T[]) => void;
  selectedValues: T[];
  search?: boolean;
  layer?: number;
}

function MultiSelectOption<T extends string>({
  option,
  setSelectedValues,
  selectedValues,
  search = true,
  layer = 0,
}: MultiSelectOptionProps<T>) {
  const { isMobile } = useMedia();

  const [open, setOpen] = React.useState<boolean>(false);
  const [debouncedOpen] = useDebounce(open, 200);

  const selected = selectedValues.some((value) => option.hasChildren !== true && value === option.value);

  const getNewValue = (prevValues: T[], newValue: T): T[] => {
    const valueSet = new Set(prevValues);
    if (valueSet.has(newValue)) {
      valueSet.delete(newValue);
    } else {
      valueSet.add(newValue);
    }
    return Array.from(valueSet);
  };

  const handleMouseEnter = () => {
    if (option.hasChildren === true) {
      setOpen(true);
    }
  };
  const handleMouseLeave = () => {
    if (option.hasChildren === true) {
      setOpen(false);
    }
  };

  const renderDisplay = () => {
    if (option.render) {
      return option.render();
    }
    return (
      <span className="truncate" title={option.label}>
        {option.label}
      </span>
    );
  };

  const selectItem = () => {
    return (
      <SelectItem
        appearance={option.appearance ?? "standard"}
        key={option.hasChildren !== true ? option.value : `${option.parent}-${option.label}`}
        value={getSearchValue(option)}
        icon={option.parent === undefined || layer !== 0 ? option.icon : undefined}
        selected={selected}
        expandable={option.hasChildren}
        disabled={option.disabled}
        hidden={option.hidden}
        id={option.id}
        onSelect={() => {
          if (option.hasChildren !== true && option.onClick) {
            option.onClick(option.value);
          }
          if (option.hasChildren !== true) {
            setSelectedValues(getNewValue(selectedValues, option.value));
          } else {
            setOpen(true);
          }
        }}
      >
        {option.parent && layer === 0 ? (
          <span className="flex min-w-0 items-center gap-1">
            <span className="truncate text-text-tertiary" title={option.parent}>
              {option.parent}
            </span>
            <RnChevronRight className="shrink-0 text-icon-tertiary" size="xs" aria-hidden="true" />
            {isTruthy(option.icon) && <span className="shrink-0">{option.icon}</span>}
            <span className="truncate text-text-secondary" title={option.label}>
              {option.label}
            </span>
          </span>
        ) : (
          renderDisplay()
        )}
      </SelectItem>
    );
  };
  const selectList = () => {
    return (
      <MultiSelectList
        setSelectedValues={setSelectedValues}
        selectedValues={selectedValues}
        data={option.hasChildren === true ? option.options : []}
        search={search}
        layer={layer + 1}
      />
    );
  };

  if (!isMobile) {
    return (
      <RemiPopover open={debouncedOpen} onOpenChange={setOpen}>
        <RemiPopoverTrigger asChild onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {selectItem()}
        </RemiPopoverTrigger>
        <RemiPopoverContent
          className="-mt-1 ml-1"
          side="right"
          align="start"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {selectList()}
        </RemiPopoverContent>
      </RemiPopover>
    );
  }

  return (
    <DropdownModal open={open} onOpenChange={setOpen}>
      <DropdownModalTrigger asChild>{selectItem()}</DropdownModalTrigger>
      <DropdownModalContent>
        <DropdownModalHeader>{option.label}</DropdownModalHeader>
        <DropdownModalBody>{selectList()}</DropdownModalBody>
        <DropdownModalFooter>
          <RemiButton
            fullWidth
            className="h-full justify-start"
            variant="tertiary"
            size="mobile"
            onClick={() => setOpen(false)}
          >
            Back
          </RemiButton>
          <span className="type-xs-italic text-tertiary">{selectedValues.length} selected</span>
        </DropdownModalFooter>
      </DropdownModalContent>
    </DropdownModal>
  );
}

interface MultiSelectListProps<T extends string> extends React.HTMLAttributes<HTMLDivElement> {
  selectedValues: T[];
  setSelectedValues: (value: T[]) => void;
  actions?: React.ReactNode;
  actionPosition?: "top" | "bottom";
  data: readonly OptionData<T>[];
  search?: boolean;
  onSearch?: (element: HTMLInputElement) => void;
  hasReset?: boolean;
  resetLabel?: string;
  hasSelectAll?: boolean;
  fill?: boolean;
  layer?: number;
  activeGroup?: boolean;
  activeLabel?: string;
}

function MultiSelectList<T extends string>({
  selectedValues,
  setSelectedValues,
  data,
  search = true,
  onSearch,
  actions,
  actionPosition = "bottom",
  hasSelectAll = false,
  hasReset = false,
  resetLabel = "Reset",
  fill = false,
  layer = 0,
  activeGroup = false,
  activeLabel = "Active",
  ...props
}: MultiSelectListProps<T>) {
  const { isMobile } = useMedia();

  const [filteredData, setFilteredData] = React.useState<readonly VerboseOptionData<T>[]>(data);
  const [searchValue, setSearchValue] = React.useState<string>("");

  const onFilter = ({ value }: HTMLInputElement) => {
    const rawValue = getRaw(value);
    setSearchValue(rawValue);
    if (!rawValue) {
      setFilteredData(data);
    } else {
      setFilteredData(Array.from(filterList(data, value)));
    }
  };

  const flatLeaves = React.useMemo(() => flattenOptions(data), [data]);

  const selectValues = (): T[] => [...new Set(flatLeaves.map((option) => option.value))];

  const hasActions = (): boolean => {
    return actions !== undefined || hasSelectAll || (hasReset && selectedValues.length > 0);
  };

  const totalValues = React.useMemo(() => new Set(flatLeaves.map((option) => option.value)).size, [flatLeaves]);

  const onInput = (element: HTMLInputElement) => {
    if (search) {
      onFilter(element);
      onSearch?.(element);
    }
  };

  const activeOptions = React.useMemo(() => {
    if (!activeGroup) {
      return [];
    }
    const selectedSet = new Set(selectedValues);
    return flatLeaves
      .filter((option) => selectedSet.has(option.value))
      .map((option) => ({ ...option, searchValue: `active ${getSearchValue(option)}` }));
  }, [activeGroup, flatLeaves, selectedValues]);

  // The !searchValue guard is load-bearing: shouldFilter={false} makes cmdk report filtered.count as
  // the mounted item count, so active rows left mounted during a search would suppress "No results".
  const showActiveGroup = activeOptions.length > 0 && !searchValue && layer === 0;

  return (
    <DropdownCommand shouldFilter={false} autoFocus tabIndex={search ? -1 : 0} className={cn(fill && "sm:w-full")}>
      <RemiCollapsible open={hasActions()}>
        <DropdownCommandInput
          isMobile={isMobile}
          onChangeCapture={(e: React.ChangeEvent<HTMLInputElement>) => onInput(e.target)}
          hidden={!search}
          placeholder="Search"
        />
        {isTruthy(actions) && actionPosition === "top" && (
          <>
            <DropdownCommandGroup>{actions}</DropdownCommandGroup>
            {filteredData.length > 0 && <DropdownCommandSeparator />}
          </>
        )}
        <DropdownCommandList {...props}>
          <DropdownCommandEmpty>No results</DropdownCommandEmpty>
          {showActiveGroup && (
            <DropdownCommandGroup heading={activeLabel} className="border-border-primary border-b">
              {activeOptions.map((option) => (
                <MultiSelectOption
                  key={`active-${option.value}`}
                  option={option}
                  setSelectedValues={setSelectedValues}
                  selectedValues={selectedValues}
                  search={false}
                  layer={0}
                />
              ))}
            </DropdownCommandGroup>
          )}
          <DropdownCommandGroup>
            {filteredData.map((option) => (
              <MultiSelectOption
                key={option.hasChildren !== true ? option.value : `${option.parent}-${option.label}`}
                option={option}
                setSelectedValues={setSelectedValues}
                selectedValues={selectedValues}
                search={false}
                layer={layer}
              />
            ))}
          </DropdownCommandGroup>
        </DropdownCommandList>
        <RemiCollapsibleContent>
          {hasSelectAll && !searchValue && (
            <>
              <DropdownCommandSeparator />
              <DropdownCommandGroup>
                {selectedValues.length === totalValues ? (
                  <SelectItem onSelect={() => setSelectedValues([])}>
                    <div className="flex w-full items-center justify-start">Deselect All</div>
                  </SelectItem>
                ) : (
                  <SelectItem onSelect={() => setSelectedValues(selectValues())}>
                    <div className="flex w-full items-center justify-start">Select All</div>
                  </SelectItem>
                )}
              </DropdownCommandGroup>
            </>
          )}
          {isTruthy(actions) && actionPosition === "bottom" && (
            <>
              {filteredData.length > 0 && <DropdownCommandSeparator />}
              <DropdownCommandGroup>{actions}</DropdownCommandGroup>
            </>
          )}
          <RemiCollapsible open={hasReset && selectedValues.length > 0 && !searchValue}>
            <RemiCollapsibleContent>
              <DropdownCommandSeparator />
              <DropdownCommandGroup>
                <SelectItem variant="destructive" onSelect={() => setSelectedValues([])}>
                  {resetLabel}
                </SelectItem>
              </DropdownCommandGroup>
            </RemiCollapsibleContent>
          </RemiCollapsible>
        </RemiCollapsibleContent>
      </RemiCollapsible>
    </DropdownCommand>
  );
}

export { MultiSelectList };
